'use client';

import { useTranslation } from 'next-i18next';
import {
  VideoCameraIcon,
  PhoneIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/solid';
import { Button } from '@components/common';

type InterviewType = 'video' | 'audio' | 'text';

interface InterviewTypeSwitcherProps {
  currentType: InterviewType;
  onSwitchType: (type: InterviewType) => void;
  className?: string;
}

/**
 * InterviewTypeSwitcher - Footer component to switch between interview types
 *
 * Shows buttons for all available interview types except the current one.
 * Allows users to change their interview type if they made the wrong selection.
 */
export const InterviewTypeSwitcher = (props: InterviewTypeSwitcherProps) => {
  const { currentType, onSwitchType, className = '' } = props;
  const { t } = useTranslation('common');

  const interviewTypes: Array<{
    type: InterviewType;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
  }> = [
    {
      type: 'video',
      label: t('ai.video.interview'),
      icon: VideoCameraIcon,
    },
    {
      type: 'audio',
      label: t('ai.audio.interview'),
      icon: PhoneIcon,
    },
    {
      type: 'text',
      label: t('ai.text.interview'),
      icon: PencilSquareIcon,
    },
  ];

  // Filter out current type and unavailable types
  const availableTypes = interviewTypes.filter(
    (item) => item.type !== currentType,
  );

  if (availableTypes.length === 0) {
    return null;
  }

  return (
    <div className={`flex flex-col items-center gap-3 ${className}`}>
      <p className="text-sm text-ink-medium">
        {t('ai.interview.change.type')}
      </p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {availableTypes.map((item) => {
          const Icon = item.icon;

          return (
            <Button
              key={item.type}
              onClick={() => onSwitchType(item.type)}
              styling="outline"
              color="primary"
              rounding="full"
              className="gap-2"
            >
              <Icon className="size-4" />
              <span className="text-sm">{item.label}</span>
            </Button>
          );
        })}
      </div>
    </div>
  );
};

